import { chunk } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import type { FileRejection } from '@mantine/dropzone';
import type { IngestionUploadRes, IngestionUploadTargetRes } from '@dealstack/shared/lib/generated';
import { calculateMD5ForFile } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/utils/calculateMD5ForFile';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { MAX_UPLOAD_FILES_PER_REQUEST } from '@dealstack/shared/lib/dealstackAi/ingestionUploads';
import {
	useCompleteUploadMutation,
	useCreateUploadsMutation,
	useIngestionUploadsQuery,
	usePutUploadMutation,
	useRemoveUploadMutation,
} from '../hooks/useIngestionQueries';
import { MAX_UPLOAD_MB } from '../ingestion.constants';
import { pagesTowardsCap } from '../ingestion.utils';
import type {
	FailedUploadPart,
	IngestionUploadsState,
	LocalUpload,
	UploadCardView,
	UploadRunProgress,
	UseIngestionUploadsOptions,
} from './useIngestionUploads.types';
import {
	hasPdfName,
	toFailedPartView,
	toLocalCard,
	toUploadedCard,
} from './useIngestionUploads.utils';

/**
 * The upload panel: the user's pending uploads on the deal, and each picked file through hash, create, PUT and
 * complete. A drop goes in requests of at most the server's per-request count; a refused one waits for Retry.
 */
export const useIngestionUploads = ({
	dealId,
	enabled,
	onUseLibraryCopy,
}: UseIngestionUploadsOptions): IngestionUploadsState => {
	const tCapManage = useTranslationCapManage();
	const [localUploads, setLocalUploads] = useState<LocalUpload[]>([]);
	const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(new Set());
	const [progress, setProgress] = useState<UploadRunProgress | null>(null);
	const [failedParts, setFailedParts] = useState<FailedUploadPart[]>([]);
	const lastRun = useRef(0);

	const uploadsQuery = useIngestionUploadsQuery(dealId, enabled);
	const createMutation = useCreateUploadsMutation();
	const putMutation = usePutUploadMutation();
	const completeMutation = useCompleteUploadMutation(dealId);
	const removeMutation = useRemoveUploadMutation(dealId);

	const patchLocal = (clientRef: string, patch: Partial<LocalUpload>) =>
		setLocalUploads((current) =>
			current.map((upload) =>
				upload.clientRef === clientRef ? { ...upload, ...patch } : upload
			)
		);

	const dropLocal = (clientRefs: string[]) =>
		setLocalUploads((current) =>
			current.filter((upload) => !clientRefs.includes(upload.clientRef))
		);

	const toastUploadFailed = () =>
		showToastV2({
			state: 'error',
			title: tCapManage('dealstack_ai.ingest.upload.toast.failed'),
		});

	const send = async (draft: LocalUpload, upload: IngestionUploadTargetRes) => {
		try {
			await putMutation.mutateAsync({ upload, file: draft.file });
		} catch {
			removeMutation.mutate(upload.fileId);
			dropLocal([draft.clientRef]);
			toastUploadFailed();
			return;
		}
		try {
			await completeMutation.mutateAsync(upload.fileId);
		} catch {
			showToastV2({
				state: 'warning',
				title: tCapManage('dealstack_ai.ingest.upload.toast.refused_title'),
				message: tCapManage('dealstack_ai.ingest.upload.toast.refused_message', {
					name: draft.file.name,
					maxMb: MAX_UPLOAD_MB,
				}),
			});
		}
		dropLocal([draft.clientRef]);
	};

	const hash = async (draft: LocalUpload): Promise<LocalUpload | null> => {
		if (draft.md5 !== null) {
			return draft;
		}
		try {
			const md5 = await calculateMD5ForFile(
				draft.file,
				tCapManage('dealstack_ai.ingest.upload.toast.read_failed')
			);
			return { ...draft, md5 };
		} catch {
			dropLocal([draft.clientRef]);
			return null;
		}
	};

	/** The request was refused or never arrived: its files leave the list and wait for Retry or Clear. */
	const holdFailedPart = (
		hashed: Array<{ draft: LocalUpload; md5: string }>,
		ignoreLibraryMatch: boolean,
		part: number,
		parts: number
	) => {
		dropLocal(hashed.map(({ draft }) => draft.clientRef));
		setFailedParts((current) => [
			...current,
			{
				id: generateUUID(),
				drafts: hashed.map(({ draft, md5 }) => ({
					...draft,
					md5,
					state: 'preparing',
					libraryMatch: null,
				})),
				ignoreLibraryMatch,
				part,
				parts,
			},
		]);
		showToastV2({
			state: 'error',
			title: tCapManage('dealstack_ai.ingest.upload.toast.part_failed_title'),
			message: tCapManage('dealstack_ai.ingest.upload.toast.part_failed_message', {
				count: hashed.length,
			}),
		});
	};

	/** One request: create, then for each file PUT and complete, or hold it on its library match. */
	const requestPart = async (
		drafts: LocalUpload[],
		ignoreLibraryMatch: boolean,
		part: number,
		parts: number
	) => {
		const hashed = (await Promise.all(drafts.map(hash))).flatMap((draft) =>
			draft === null || draft.md5 === null ? [] : [{ draft, md5: draft.md5 }]
		);
		if (hashed.length === 0) {
			return;
		}

		const created = await createMutation
			.mutateAsync({
				files: hashed.map(({ draft, md5 }) => ({
					clientRef: draft.clientRef,
					fileName: draft.file.name,
					sizeBytes: draft.file.size,
					md5,
					...(ignoreLibraryMatch ? { ignoreLibraryMatch: true } : {}),
				})),
			})
			.catch(() => undefined);
		if (created === undefined) {
			holdFailedPart(hashed, ignoreLibraryMatch, part, parts);
			return;
		}

		await Promise.all(
			created.files.map(async ({ clientRef, libraryMatch, upload }) => {
				const match = hashed.find(({ draft }) => draft.clientRef === clientRef);
				if (match === undefined) {
					return;
				}
				const draft = { ...match.draft, md5: match.md5 };
				if (upload !== null) {
					patchLocal(clientRef, { state: 'uploading', md5: match.md5 });
					await send(draft, upload);
					return;
				}
				patchLocal(clientRef, { state: 'library_match', libraryMatch, md5: match.md5 });
			})
		);
	};

	/** The drop's requests in drop order, each sent once the previous one has settled. */
	const requestUploads = async (drafts: LocalUpload[], ignoreLibraryMatch: boolean) => {
		const parts = chunk(drafts, MAX_UPLOAD_FILES_PER_REQUEST);
		lastRun.current += 1;
		const run = lastRun.current;
		for (const [index, part] of parts.entries()) {
			if (parts.length > 1) {
				setProgress({ run, view: { part: index + 1, parts: parts.length } });
			}
			await requestPart(part, ignoreLibraryMatch, index + 1, parts.length);
		}
		setProgress((current) => (current?.run === run ? null : current));
	};

	const rejectFiles = (count: number) =>
		showToastV2({
			state: 'warning',
			title: tCapManage('dealstack_ai.ingest.upload.toast.rejected_title'),
			message: tCapManage('dealstack_ai.ingest.upload.toast.rejected_message', {
				count,
				maxMb: MAX_UPLOAD_MB,
			}),
		});

	const onDrop = (files: File[]) => {
		const accepted = files.filter(hasPdfName);
		if (accepted.length < files.length) {
			rejectFiles(files.length - accepted.length);
		}
		if (accepted.length === 0) {
			return;
		}
		const drafts = accepted.map(
			(file): LocalUpload => ({
				clientRef: generateUUID(),
				file,
				state: 'preparing',
				libraryMatch: null,
				md5: null,
			})
		);
		setLocalUploads((current) => [...current, ...drafts]);
		void requestUploads(drafts, false);
	};

	const serverFiles = uploadsQuery.data?.files;
	const listedFiles = useMemo(
		(): IngestionUploadRes[] =>
			(serverFiles ?? []).filter((file) => !removedIds.has(file.fileId)),
		[serverFiles, removedIds]
	);

	const cards = useMemo(
		(): UploadCardView[] => [
			...listedFiles.map(toUploadedCard),
			...localUploads.map(toLocalCard),
		],
		[listedFiles, localUploads]
	);

	const readyFiles = listedFiles.filter((file) => file.pages !== null);

	const findLocal = (key: string) => localUploads.find((upload) => upload.clientRef === key);

	return {
		cards,
		readyFileIds: readyFiles.map((file) => file.fileId),
		readyPages: readyFiles.reduce((total, file) => total + pagesTowardsCap(file.pages), 0),
		isUploading: localUploads.some((upload) => upload.state !== 'library_match'),
		progress: progress?.view ?? null,
		failedParts: failedParts.map(toFailedPartView),
		isError: uploadsQuery.isError,
		onDrop,
		onReject: (rejections: FileRejection[]) => rejectFiles(rejections.length),
		onRemove: (key) => {
			if (findLocal(key) !== undefined) {
				dropLocal([key]);
				return;
			}
			setRemovedIds((current) => new Set([...current, key]));
			removeMutation.mutate(key, {
				onError: () => {
					setRemovedIds((current) => new Set([...current].filter((id) => id !== key)));
					showToastV2({
						state: 'error',
						title: tCapManage('dealstack_ai.ingest.upload.toast.remove_failed'),
					});
				},
			});
		},
		onUseLibraryCopy: (key) => {
			const documentId = findLocal(key)?.libraryMatch?.documentId;
			if (documentId === undefined) {
				return;
			}
			onUseLibraryCopy(documentId);
			dropLocal([key]);
		},
		onUploadAnyway: (key) => {
			const draft = findLocal(key);
			if (draft === undefined) {
				return;
			}
			patchLocal(key, { state: 'preparing', libraryMatch: null });
			void requestUploads([{ ...draft, state: 'preparing', libraryMatch: null }], true);
		},
		onRetryPart: (id) => {
			const failed = failedParts.find((part) => part.id === id);
			if (failed === undefined) {
				return;
			}
			setFailedParts((current) => current.filter((part) => part.id !== id));
			setLocalUploads((current) => [...current, ...failed.drafts]);
			void requestUploads(failed.drafts, failed.ignoreLibraryMatch);
		},
		onClearPart: (id) => setFailedParts((current) => current.filter((part) => part.id !== id)),
	};
};
